import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Menu,
  ChevronDown,
  X,
  Factory,
  Layers,
  Settings,
  Tv,
  Building,
  Users,
  Briefcase,
  Target,
  Globe,
  Calendar,
  Handshake,
  ArrowRight,
  Newspaper,
  Wrench,
  HardDrive,
  SmartphoneIcon
} from "lucide-react";
import logo from '../../assets/images/BPL_Logo.png'
import article from '../../assets/images/bhagwati-article.avif'
const menuItems = {
  About: [
    { title: "Company Overview", icon: Factory, path: "/about" },
    { title: "Our Vision and Mission", icon: Target, path: "/vision-mission" },
    { title: "Our Presence", icon: Globe, path: "/factories" },
    { title: "Timeline and Milestones", icon: Calendar, path: "/history" },
    { title: "Our Leadership", icon: Users, path: "/team" },
    { title: "Our Partners", icon: Handshake, path: "/partner" }
  ],
  Services: [
    { title: "Our Divisions", icon: Layers, path: "/manufacturing" },
    // { title: "Smt Manufacturing", icon: Settings, path: "/smt-manufacturing" },
    { title: "LED TV Manufacturing", icon: Tv, path: "/led-manufacturing" },
    { title: "Mobile Phones", icon: SmartphoneIcon, path: "/mobile" },
    { title: "Tablets", icon: SmartphoneIcon, path: "/tablet" }
  ],
  Company: [
    { title: "Partnership Directory", icon: Building, path: "/partners" },
    { title: "Responsibility", icon: Users, path: "/responsibility" },
    { title: "Careers", icon: Briefcase, path: "/careers" }
  ],
};

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const navRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        setActiveMenu(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handleMenuEnter = (menu) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setActiveMenu(menu);
  };

  const handleMenuLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setActiveMenu(null);
    }, 100);
  };

  const handleMenuItemClick = () => {
    setActiveMenu(null);
    setMobileMenuOpen(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white" ref={navRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex-shrink-0">
            <img src={logo} alt="Logo" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {Object.keys(menuItems).map((section) => (
              <div
                key={section}
                className="relative group"
                onMouseEnter={() => handleMenuEnter(section)}
                onMouseLeave={handleMenuLeave}
              >
                <button className="group inline-flex items-center text-sm font-medium text-gray-700 hover:text-black transition-colors duration-300">
                  {section}
                  <ChevronDown
                    className={`ml-1 h-4 w-4 transition-transform duration-300 ${
                      activeMenu === section ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
            ))}
            <Link
              to="/blogs"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-black transition-colors duration-300"
            >
              Media & News
              <Newspaper className="ml-1 h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-full text-white bg-black hover:bg-gray-800 transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>

          <button
            className="md:hidden p-2 rounded-md text-gray-700 hover:text-black hover:bg-gray-100 focus:outline-none transition-colors duration-300"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Modern Mega Menu */}
      <div
        className={`absolute left-0 w-full bg-white border-t border-gray-100 transform transition-all duration-300 ease-in-out ${
          activeMenu ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"
        }`}
        onMouseEnter={() => handleMenuEnter(activeMenu)}
        onMouseLeave={handleMenuLeave}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-0">
            {/* Left Section - Navigation Items */}
            <div className="col-span-8 p-8 bg-white">
              <div className="grid grid-cols-2 gap-y-2 gap-x-12">
                {activeMenu && menuItems[activeMenu].map((item, idx) => (
                  <Link
                    key={idx}
                    to={item.path}
                    className="group flex items-center p-4 rounded-xl hover:bg-gray-50 transition-all duration-300"
                    onClick={handleMenuItemClick}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-gray-50 group-hover:bg-white group-hover:shadow-sm transition-all duration-300">
                        <item.icon className="h-5 w-5 text-gray-600 group-hover:text-black transition-colors duration-300" />
                      </div>
                      <div className="flex items-center justify-between flex-1">
                        <span className="text-sm font-medium text-gray-900 group-hover:text-black transition-colors duration-300">
                          {item.title}
                        </span>
                        <ArrowRight className="h-4 w-4 text-gray-400 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-black transition-all duration-300" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
            
            {/* Right Section - Featured Content */}
            <div className="col-span-4 bg-gray-50">
              <div className="p-8 h-full">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Featured</h3>
                <div className="relative rounded-xl overflow-hidden">
                  <img
                    src={article}
                    alt="Featured"
                    className="w-full h-48 object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h4 className="text-white text-sm font-medium mb-1">Latest Update</h4>
                    <p className="text-gray-200 text-xs">Bhagwati-Huaqin manufacturing joint venture gets government nod; to make smartphones for Vivo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden fixed inset-0 bg-gray-900 bg-opacity-50 z-50 transition-opacity duration-300 ${
          mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className={`fixed inset-y-0 right-0 w-full max-w-sm bg-white shadow-xl transform transition-transform duration-300 ease-out ${
            mobileMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="sticky top-0 z-50 flex justify-end p-4 bg-white border-b">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-md hover:bg-gray-100 transition-colors duration-300"
            >
              <X className="h-6 w-6 text-gray-600" />
            </button>
          </div>

          <div className="h-full overflow-y-auto pt-2">
            <div className="px-4 py-6 space-y-6">
              {Object.entries(menuItems).map(([section, items]) => (
                <div key={section} className="space-y-4">
                  <button
                    className="flex items-center justify-between w-full text-left transition-colors duration-300"
                    onClick={() => setActiveMenu(activeMenu === section ? null : section)}
                  >
                    <span className="text-lg font-medium text-gray-900">
                      {section}
                    </span>
                    <ChevronDown
                      className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${
                        activeMenu === section ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  <div
                    className={`space-y-4 transition-all duration-300 ${
                      activeMenu === section ? "block" : "hidden"
                    }`}
                  >
                    {items.map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.path}
                        className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-all duration-300"
                        onClick={handleMenuItemClick}
                      >
                        <item.icon className="h-5 w-5 text-gray-400 mr-3 group-hover:text-black transition-colors duration-300" />
                        <span className="text-base font-medium text-gray-900">
                          {item.title}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
              <div className="space-y-4">
                <Link
                  to="/blogs"
                  className="block py-2 text-base font-medium text-gray-900 hover:text-black transition-colors duration-300"
                  onClick={handleMenuItemClick}
                >
                  Media & News
                </Link>
                <Link
                  to="/contact"
                  className="block w-full py-3 px-4 rounded-full text-center text-white bg-black hover:bg-gray-800 transition-colors duration-300"
                  onClick={handleMenuItemClick}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}